import { useState, useRef, useCallback } from "react";
import { Download, Loader2 } from "lucide-react";

const CARD_SIZE = 1080;
const AMBER = "#C17F24";
const CHARCOAL = "#1F2937";

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let currentLine = "";
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

export default function InstagramCard({ story }) {
  const [generating, setGenerating] = useState(false);
  const canvasRef = useRef(null);

  const generateCard = useCallback(async () => {
    setGenerating(true);

    try {
      const canvas = document.createElement("canvas");
      canvas.width = CARD_SIZE;
      canvas.height = CARD_SIZE;
      const ctx = canvas.getContext("2d");

      // Background
      ctx.fillStyle = CHARCOAL;
      ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);

      // Load and draw image
      if (story.media_url && story.media_type === "image") {
        try {
          const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = story.media_url;
          });

          // Draw image covering full canvas
          const imgRatio = img.width / img.height;
          let sx = 0, sy = 0, sw = img.width, sh = img.height;
          if (imgRatio > 1) {
            sx = (img.width - img.height) / 2;
            sw = img.height;
          } else {
            sy = (img.height - img.width) / 2;
            sh = img.width;
          }
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, CARD_SIZE, CARD_SIZE);

          // Warm amber overlay
          ctx.fillStyle = "rgba(193, 127, 36, 0.18)";
          ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);

          // Dark gradient from bottom for text readability
          const gradient = ctx.createLinearGradient(0, CARD_SIZE * 0.35, 0, CARD_SIZE);
          gradient.addColorStop(0, "rgba(31, 41, 55, 0)");
          gradient.addColorStop(0.4, "rgba(31, 41, 55, 0.5)");
          gradient.addColorStop(1, "rgba(31, 41, 55, 0.92)");
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);
        } catch {
          // If image fails, use solid background
          ctx.fillStyle = CHARCOAL;
          ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);
          // Subtle amber accent bar
          ctx.fillStyle = "rgba(193, 127, 36, 0.15)";
          ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);
        }
      } else {
        // No image — charcoal with amber accent
        ctx.fillStyle = CHARCOAL;
        ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);
        ctx.fillStyle = "rgba(193, 127, 36, 0.08)";
        ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);

        // Decorative opening quote
        ctx.fillStyle = "rgba(193, 127, 36, 0.15)";
        ctx.font = "bold 400px Georgia, serif";
        ctx.fillText("\u201C", 40, 350);
      }

      const padding = 72;
      const textAreaBottom = CARD_SIZE - padding;
      let y = textAreaBottom;

      // Logo — bottom right
      ctx.font = "bold 24px 'Plus Jakarta Sans', system-ui, sans-serif";
      const logoIam = "iam";
      const logoBecause = "because";
      const logoIamWidth = ctx.measureText(logoIam).width;
      const logoBecauseWidth = ctx.measureText(logoBecause).width;
      const logoTotalWidth = logoIamWidth + logoBecauseWidth;
      const logoX = CARD_SIZE - padding - logoTotalWidth;
      ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
      ctx.fillText(logoIam, logoX, y);
      ctx.fillStyle = AMBER;
      ctx.fillText(logoBecause, logoX + logoIamWidth, y);

      y -= 52;

      // "Why" text — white serif, first 2-3 lines
      const whyClean = story.why.replace(/\n+/g, " ").trim();
      ctx.font = "italic 36px 'Playfair Display', Georgia, serif";
      ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
      const maxTextWidth = CARD_SIZE - padding * 2;
      const whyLines = wrapText(ctx, `\u201C${whyClean}\u201D`, maxTextWidth);
      const maxLines = 4;
      let displayLines = whyLines.slice(0, maxLines);
      if (whyLines.length > maxLines) {
        let lastLine = displayLines[maxLines - 1];
        if (lastLine.length > 3) lastLine = lastLine.slice(0, -3) + "...\u201D";
        displayLines[maxLines - 1] = lastLine;
      }

      // Draw why lines bottom-up
      const lineHeight = 48;
      y -= (displayLines.length - 1) * lineHeight;
      for (let i = 0; i < displayLines.length; i++) {
        ctx.fillText(displayLines[i], padding, y + i * lineHeight);
      }

      y -= 36;

      // Role — amber
      ctx.font = "500 26px 'Plus Jakarta Sans', system-ui, sans-serif";
      ctx.fillStyle = AMBER;
      const roleText = story.role + (story.city && story.country ? ` \u2014 ${story.city}, ${story.country}` : "");
      ctx.fillText(roleText, padding, y);

      y -= 40;

      // Name — white, uppercase, tracked
      ctx.font = "bold 30px 'Plus Jakarta Sans', system-ui, sans-serif";
      ctx.fillStyle = "#FFFFFF";
      ctx.letterSpacing = "4px";
      ctx.fillText(story.name.toUpperCase(), padding, y);

      // Amber accent line above name
      y -= 20;
      ctx.fillStyle = AMBER;
      ctx.fillRect(padding, y, 60, 3);

      // Download
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `iambecause-${story.name.toLowerCase().replace(/\s+/g, "-")}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setGenerating(false);
      }, "image/png");
    } catch (err) {
      console.error("Card generation failed:", err);
      setGenerating(false);
    }
  }, [story]);

  return (
    <button
      data-testid={`instagram-card-${story.id}`}
      onClick={generateCard}
      disabled={generating}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
    >
      {generating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
      {generating ? "Generating..." : "IG Card"}
    </button>
  );
}
